import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatDateTimeKo, formatKRW, formatDateKo } from '@/lib/format';

interface BookingDetail {
  id: string;
  status: string;
  starts_at: string;
  duration_minutes: number;
  price_krw: number;
  payment_method: string;
  shaman_display_name: string;
  shaman_slug: string;
  shaman_bio: string | null;
  shaman_bank_name: string | null;
  shaman_bank_account_holder: string | null;
  va_bank: string | null;
  va_account_number: string | null;
  va_due_date: string | null;
  hold_expires_at: string | null;
  cancelled_at: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  pending_payment: '결제/입금 대기',
  confirmed: '예약 확정',
  cancelled_by_customer: '취소됨 (고객)',
  cancelled_by_shaman: '취소됨 (무당)',
  no_show: '노쇼',
  completed: '완료',
  expired: '결제 기한 만료',
};

export function BookingDetailPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [params] = useSearchParams();
  const token = params.get('token');

  const { data, isLoading, error } = useQuery({
    queryKey: ['booking', bookingId, token],
    enabled: !!bookingId && !!token,
    queryFn: async (): Promise<BookingDetail | null> => {
      const { data, error } = await supabase.rpc('get_booking_by_token', {
        p_booking_id: bookingId!,
        p_token: token!,
      });
      if (error) throw error;
      const rows = data as BookingDetail[];
      return rows?.[0] ?? null;
    },
  });

  if (!bookingId || !token) {
    return <div className="card text-danger">예약 정보가 잘못되었습니다.</div>;
  }
  if (isLoading) return <div className="text-ink-muted text-center py-8">불러오는 중…</div>;
  if (error || !data) {
    return <div className="card text-danger">예약을 찾을 수 없습니다.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="card space-y-3">
        <h1 className="text-2xl font-bold">{data.shaman_display_name}</h1>
        <div className="text-base text-ink-muted">상태: <span className="font-semibold text-ink">{STATUS_LABEL[data.status] ?? data.status}</span></div>
        <div className="rounded-lg bg-surface-alt p-4 space-y-1">
          <div className="text-base text-ink-muted">예약 일시</div>
          <div className="text-xl font-bold">{formatDateTimeKo(data.starts_at)}</div>
          <div className="text-base">{data.duration_minutes}분 · {formatKRW(data.price_krw)}</div>
        </div>
      </div>

      {data.status === 'pending_payment' && data.payment_method === 'virtual_account' && (
        <div className="card space-y-2">
          <h2 className="text-lg font-bold">입금 안내</h2>
          {data.va_bank && data.va_account_number ? (
            <>
              <div className="text-base">
                {data.va_bank} <span className="font-mono">{data.va_account_number}</span>
              </div>
              <div className="text-base text-ink-muted">
                {data.va_due_date && (
                  <>입금 기한: {formatDateKo(data.va_due_date, 'M월 d일(EEE) HH:mm')}</>
                )}
              </div>
              <p className="text-base text-ink-muted">
                금액을 정확히 입금하시면 자동으로 예약이 확정됩니다.
              </p>
            </>
          ) : (
            <p className="text-base text-ink-muted">
              가상계좌 발급 중입니다. 잠시 후 새로고침하면 입금 안내가 나타납니다.
            </p>
          )}
        </div>
      )}

      {data.status === 'pending_payment' && data.payment_method === 'toss_pay' && (
        <div className="card text-base text-ink-muted">
          결제가 완료되지 않았습니다. 결제 페이지로 다시 이동해주세요.
        </div>
      )}

      {data.status === 'confirmed' && (
        <div className="card space-y-2">
          <h2 className="text-lg font-bold">안내</h2>
          {data.shaman_bio && (
            <p className="text-base whitespace-pre-line">{data.shaman_bio}</p>
          )}
        </div>
      )}

      {data.status !== 'cancelled_by_customer' &&
        data.status !== 'cancelled_by_shaman' &&
        data.status !== 'expired' &&
        data.status !== 'no_show' &&
        data.status !== 'completed' && (
          <Link
            to={`/cancel/${token}?booking=${bookingId}`}
            className="btn-secondary w-full"
          >
            예약 취소하기
          </Link>
        )}

      <div className="text-base text-ink-muted text-center">
        예약 접수 완료. 휴대전화로 안내 문자가 발송됩니다 (Solapi 연동은 다음 업데이트에서 활성화).
      </div>
    </div>
  );
}
